/**
 * Timer management hooks for Svelte 5
 * Provides automatic cleanup and memory leak prevention for timers
 */

import { onDestroy } from 'svelte';

/**
 * setTimeout hook with automatic cleanup
 * @param callback - Function to execute after delay
 * @param delay - Delay in milliseconds
 * @param autoStart - Whether to start timer immediately (default: true)
 * @returns Object with start, stop, and restart methods
 */
export function useTimeout(
  callback: () => void,
  delay: number,
  autoStart: boolean = true
) {
  let timeoutId: NodeJS.Timeout | null = null;
  
  function start() {
    stop(); // Clear any existing timer
    timeoutId = setTimeout(() => {
      callback();
      timeoutId = null;
    }, delay);
  }
  
  function stop() {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  }
  
  function restart() {
    stop();
    start();
  }
  
  // Auto-start if requested
  if (autoStart) {
    start();
  }
  
  // Cleanup on component destroy
  onDestroy(() => {
    stop();
  });
  
  return {
    start,
    stop,
    restart,
    isActive: () => timeoutId !== null
  };
}

/**
 * setInterval hook with automatic cleanup
 * @param callback - Function to execute on each interval
 * @param interval - Interval in milliseconds
 * @param autoStart - Whether to start timer immediately (default: false)
 * @returns Object with start, stop, and restart methods
 */
export function useInterval(
  callback: () => void,
  interval: number,
  autoStart: boolean = false
) {
  let intervalId: NodeJS.Timeout | null = null;
  
  function start() {
    stop(); // Clear any existing timer
    intervalId = setInterval(callback, interval);
  }
  
  function stop() {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  }
  
  function restart() {
    stop();
    start();
  }
  
  // Auto-start if requested
  if (autoStart) {
    start();
  }
  
  // Cleanup on component destroy
  onDestroy(() => {
    stop();
  });
  
  return {
    start,
    stop,
    restart,
    isActive: () => intervalId !== null
  };
}

/**
 * Debounced function execution with automatic cleanup
 * @param callback - Function to debounce
 * @param delay - Debounce delay in milliseconds
 * @returns Debounced function
 */
export function useDebounce<T extends (...args: any[]) => any>(
  callback: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout | null = null;
  
  function debounced(...args: Parameters<T>) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    timeoutId = setTimeout(() => {
      callback(...args);
      timeoutId = null;
    }, delay);
  }
  
  // Cleanup on component destroy
  onDestroy(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  });
  
  return debounced;
}

/**
 * Throttled function execution with automatic cleanup
 * @param callback - Function to throttle
 * @param limit - Minimum time between executions in milliseconds
 * @returns Throttled function
 */
export function useThrottle<T extends (...args: any[]) => any>(
  callback: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle = false;
  let lastArgs: Parameters<T> | null = null;
  let timeoutId: NodeJS.Timeout | null = null;
  
  function throttled(...args: Parameters<T>) {
    if (!inThrottle) {
      callback(...args);
      inThrottle = true;
      
      timeoutId = setTimeout(() => {
        inThrottle = false;
        if (lastArgs) {
          throttled(...lastArgs);
          lastArgs = null;
        }
      }, limit);
    } else {
      lastArgs = args;
    }
  }
  
  // Cleanup on component destroy
  onDestroy(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  });
  
  return throttled;
}

/**
 * Delayed execution with automatic cleanup
 * Useful for showing temporary messages, tooltips, etc.
 * @param delay - Delay in milliseconds
 * @returns Promise that resolves after delay
 */
export function useDelay(delay: number): Promise<void> {
  let timeoutId: NodeJS.Timeout | null = null;
  
  const promise = new Promise<void>((resolve) => {
    timeoutId = setTimeout(() => {
      timeoutId = null;
      resolve();
    }, delay);
  });
  
  // Cleanup on component destroy
  onDestroy(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  });
  
  return promise;
}

/**
 * Countdown timer hook
 * @param duration - Duration in seconds
 * @param onComplete - Callback when countdown completes
 * @param autoStart - Whether to start immediately
 * @returns Object with current time, start, stop, restart methods
 */
export function useCountdown(
  duration: number,
  onComplete?: () => void,
  autoStart: boolean = false
) {
  let timeLeft = duration;
  let intervalId: NodeJS.Timeout | null = null;
  let updateCallback: ((time: number) => void) | null = null;
  
  function start() {
    stop();
    timeLeft = duration;
    updateCallback?.(timeLeft);
    
    intervalId = setInterval(() => {
      timeLeft--;
      updateCallback?.(timeLeft);
      
      if (timeLeft <= 0) {
        stop();
        onComplete?.();
      }
    }, 1000);
  }
  
  function stop() {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  }
  
  function restart() {
    stop();
    start();
  }
  
  function reset() {
    stop();
    timeLeft = duration;
    updateCallback?.(timeLeft);
  }
  
  function onUpdate(callback: (time: number) => void) {
    updateCallback = callback;
    callback(timeLeft);
  }
  
  if (autoStart) {
    start();
  }
  
  // Cleanup on component destroy
  onDestroy(() => {
    stop();
  });
  
  return {
    get timeLeft() { return timeLeft; },
    start,
    stop,
    restart,
    reset,
    isActive: () => intervalId !== null,
    onUpdate
  };
}

/**
 * Auto-hide timer for notifications, tooltips, etc.
 * @param duration - Duration to show in milliseconds
 * @param onHide - Callback when should hide
 * @returns Object with show, hide, and extend methods
 */
export function useAutoHide(
  duration: number,
  onHide: () => void
) {
  let timeoutId: NodeJS.Timeout | null = null;
  
  function show() {
    hide(); // Clear any existing timer
    timeoutId = setTimeout(() => {
      onHide();
      timeoutId = null;
    }, duration);
  }
  
  function hide() {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  }
  
  function extend(additionalTime?: number) {
    if (timeoutId) {
      hide();
      timeoutId = setTimeout(() => {
        onHide();
        timeoutId = null;
      }, additionalTime || duration);
    }
  }
  
  // Cleanup on component destroy
  onDestroy(() => {
    hide();
  });
  
  return {
    show,
    hide,
    extend,
    isActive: () => timeoutId !== null
  };
}

/**
 * Polling hook for periodic data fetching
 * @param callback - Function to call on each poll
 * @param interval - Polling interval in milliseconds
 * @param options - Polling options
 * @returns Object with start, stop, restart methods
 */
export function usePolling(
  callback: () => void | Promise<void>,
  interval: number,
  options: {
    autoStart?: boolean;
    immediate?: boolean;
    onError?: (error: Error) => void;
  } = {}
) {
  const { autoStart = false, immediate = false, onError } = options;
  let intervalId: NodeJS.Timeout | null = null;
  let isPolling = false;
  
  async function poll() {
    if (isPolling) return;
    
    isPolling = true;
    try {
      await callback();
    } catch (error) {
      onError?.(error as Error);
    } finally {
      isPolling = false;
    }
  }
  
  function start() {
    stop();
    
    if (immediate) {
      poll();
    }
    
    intervalId = setInterval(poll, interval);
  }
  
  function stop() {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  }
  
  function restart() {
    stop();
    start();
  }
  
  if (autoStart) {
    start();
  }
  
  // Cleanup on component destroy
  onDestroy(() => {
    stop();
  });
  
  return {
    start,
    stop,
    restart,
    isActive: () => intervalId !== null,
    isPolling: () => isPolling
  };
}